import { Check } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cursorTip } from "@/components/shared/CursorTip";
import { resolveAvatarUrl } from "@/lib/avatarUtils";
import { getUserInitials } from "@/lib/userUtils";
import { cn } from "@/lib/utils";
import type { StackPerson } from "@/components/shared/AssigneeStack";

/** One selectable face. `label` is the resolved display name for the tip. */
export interface FacePerson {
  id: string;
  label: string;
  profile: StackPerson;
}

interface PersonFaceGridProps {
  people: FacePerson[];
  /** Currently filtered-on ids. Multi-select — this is a set, not a choice. */
  selected: string[];
  onToggle: (id: string) => void;
  /** Floated first and tipped as "Me — <name>". */
  currentUserId?: string | null;
  /** Announced on the group, since the faces carry no visible text. */
  ariaLabel: string;
  className?: string;
}

/**
 * A roster as a grid of faces, not a list of names.
 *
 * A vertical list costs one ~32px row per person, so a 30-person tenant is a
 * 960px column inside a popover — a scroll journey to answer "whose tickets?".
 * The same 30 people as 28px faces, seven to a row, is five rows and about
 * 170px: the whole roster visible at once, which is what makes picking two or
 * three of them a glance instead of a search.
 *
 * The trade is that a face is only recognisable to someone who already knows
 * the person, so the name has to stay reachable — hence a CursorTip on every
 * face rather than a caption under it. A caption would put the list's height
 * straight back.
 *
 * Selection state has to survive being read at 28px, so it is carried twice:
 * a brand ring around the face AND a check badge on its shoulder. The ring
 * alone is a 2px colour difference at thumbnail size; the badge alone is easy
 * to mistake for part of an avatar. Once anything is selected the unpicked
 * faces drop to 45% so the picked ones are the figure and the rest the ground
 * — dimming from the start would just make an untouched filter look disabled.
 *
 * Every face is a real `<button aria-pressed>` with the name as its
 * `aria-label`. A grid whose entire content is images is invisible to a screen
 * reader otherwise, and this is a filter control, not decoration.
 */
const PersonFaceGrid = ({ people, selected, onToggle, currentUserId, ariaLabel, className }: PersonFaceGridProps) => {
  const anySelected = selected.length > 0;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      // auto-fill, NOT a fixed `grid-cols-7`.
      //
      // Seven 28px faces plus gutters is what the popover fits — until the
      // roster passes ~30, the box starts scrolling, and the vertical scrollbar
      // takes its slice out of the content width. A pinned column count can't
      // know that happened, so the seventh column overflows and the grid grows
      // a HORIZONTAL scrollbar too: the layout breaks precisely at the roster
      // size this grid was built for. Measured at 32 faces before this changed.
      //
      // auto-fill re-solves the count against whatever width actually remains —
      // seven normally, six once a scrollbar appears, more on a wider host. A
      // bigger roster just wraps into more rows; it never reflows into a list,
      // because the density is the entire reason this is a grid.
      className={cn("grid grid-cols-[repeat(auto-fill,minmax(1.75rem,1fr))] gap-2", className)}
    >
      {people.map((person) => {
        const isMe = person.id === currentUserId;
        const isSelected = selected.includes(person.id);

        return (
          <button
            key={person.id}
            type="button"
            onClick={() => onToggle(person.id)}
            aria-pressed={isSelected}
            aria-label={isMe ? `Me — ${person.label}` : person.label}
            // The tip is the only place the name appears, so it opens at once:
            // the delay that stops flicker on a table of truncated titles would
            // here be a pause on every lookup the grid exists to serve.
            {...cursorTip(person.label, isMe ? "Me —" : undefined)}
            className={cn(
              "press-pop relative mx-auto rounded-full p-0.5 transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isSelected ? "ring-2 ring-primary" : "hover:ring-2 hover:ring-primary/40",
              anySelected && !isSelected && "opacity-45 hover:opacity-100",
            )}
          >
            {/* Deliberately NOT <UserAvatar>: that primitive hands `avatarUrl`
                straight to the <img>, and every seeded profile stores a
                `/src/assets/avatars/*.png` path that only resolves through
                `resolveAvatarUrl`'s bundle map. Raw, those faces 404 in a built
                bundle and the whole grid silently falls back to initials. */}
            <Avatar className="block h-7 w-7 shrink-0">
              {person.profile.avatar_url && <AvatarImage src={resolveAvatarUrl(person.profile.avatar_url)} alt="" />}
              <AvatarFallback className="avatar-plate text-[10px] font-medium text-primary dark:text-blue-200">
                {getUserInitials(person.profile)}
              </AvatarFallback>
            </Avatar>
            {isSelected && (
              <span
                // A ring in the CARD's colour, not a border: it has to punch a
                // gap between the badge and the face it overlaps, whatever the
                // avatar underneath happens to be.
                className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-card"
                aria-hidden
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PersonFaceGrid;
